import {
  useMemo,
  useRef,
  useEffect,
  useCallback,
  useLayoutEffect
} from "react";
import { useMessages, useConversation } from "../api/hooks";
import { useCrmContact } from "../api/crm";
import { useInboxContext } from "../context/useInboxContext";
import type { ComposerTab } from "../components/constants";

interface CrmContactLike {
  first_name: string;
  last_name: string;
  emails: string[];
  phones: string[];
}

const resolveContactName = (crmContact: CrmContactLike | undefined) => {
  if (!crmContact) return "Unknown";

  // Filter null/empty parts so the template literal doesn't coerce nulls
  // to the literal string "null" — fresh SMS-created contacts can have
  // both first_name and last_name = NULL.
  const displayName = [crmContact.first_name, crmContact.last_name]
    .filter((part): part is string => !!part && part.trim().length > 0)
    .join(" ")
    .trim();
  const primaryEmail = crmContact.emails[0] || null;
  const primaryPhone = crmContact.phones[0] || null;

  return displayName || primaryEmail || primaryPhone || "Unknown";
};

/** Channels the messages endpoint can filter by — it has no `voice` filter. */
type MessageChannelFilter = "email" | "sms" | "note" | "chat";

const CHANNEL_BY_TAB: Record<ComposerTab, MessageChannelFilter | undefined> = {
  email: "email",
  sms: "sms",
  note: "note",
  chat: "chat",
  voice: undefined
};

interface ThreadFilterParams {
  from?: string;
  to?: string;
  q?: string;
}

const buildMessageParams = (
  targetMessageId?: string,
  channel?: MessageChannelFilter,
  threadFilter?: ThreadFilterParams
) => {
  const base: {
    anchor?: string;
    channel?: MessageChannelFilter;
    from?: string;
    to?: string;
    q?: string;
  } = {};
  if (targetMessageId) base.anchor = targetMessageId;
  if (channel) base.channel = channel;
  if (threadFilter?.from) base.from = threadFilter.from;
  if (threadFilter?.to) base.to = threadFilter.to;
  if (threadFilter?.q) base.q = threadFilter.q;
  return base;
};

/**
 * @param activeTab which composer channel is selected — the Inbox route reads
 *   it from `?tab=`, the message pop-up from its own chip state.
 * @param options.filterByChannel loads only `activeTab`'s channel instead of
 *   the combined thread. The Inbox shows every channel interleaved; the pop-up's
 *   chips each show one channel (CRM-1133). Filtering server-side keeps
 *   pagination honest — a client-side filter can hand back an empty page while
 *   older history still exists.
 */
export const useInboxDetailPanelData = (
  conversationId?: string,
  activeTab: ComposerTab = "email",
  options?: { filterByChannel?: boolean; threadFilter?: ThreadFilterParams }
) => {
  const channelFilter = options?.filterByChannel
    ? CHANNEL_BY_TAB[activeTab]
    : undefined;
  const { threadFilter } = options ?? {};
  const {
    state: { targetMessageId, initialUnreadCount, draftContactId }
  } = useInboxContext();

  const isDraft = !conversationId && draftContactId !== undefined;
  const isAnchorMode = !!targetMessageId;

  const messageParams = useMemo(
    () => buildMessageParams(targetMessageId, channelFilter, threadFilter),
    [
      targetMessageId,
      channelFilter,
      threadFilter?.from,
      threadFilter?.to,
      threadFilter?.q
    ]
  );

  const {
    data: messagesData,
    isLoading: isLoadingMessages,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage
  } = useMessages(conversationId || "", messageParams);

  const { data: conversationData } = useConversation(conversationId || "");

  const messages =
    messagesData?.pages.flatMap((page) => page.messages).filter(Boolean) || [];
  const pageCount = messagesData?.pages.length ?? 0;
  const lastMessage = messages[messages.length - 1];
  const lastMessageKey = lastMessage
    ? [
        lastMessage.id,
        lastMessage.status,
        lastMessage.attachments?.length ?? 0,
        lastMessage.chat_details?.sent_as_sms ? 1 : 0,
        lastMessage.chat_details?.sent_as_sms_failed_reason ? 1 : 0
      ].join(":")
    : undefined;

  const conversation = conversationData?.conversation;
  const contactId = isDraft
    ? String(draftContactId)
    : conversation?.contact_id.toString();
  const { data: crmContact } = useCrmContact(contactId || "");

  const contactName = useMemo(
    () => resolveContactName(crmContact),
    [crmContact]
  );

  return {
    targetMessageId,
    initialUnreadCount,
    draftContactId,
    isDraft,
    isAnchorMode,
    messages,
    pageCount,
    lastMessageKey,
    isLoadingMessages,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
    contactId,
    contactName
  };
};

interface UseMessagesScrollParams {
  conversationId?: string;
  pageCount: number;
  lastMessageKey?: string;
  isAnchorMode: boolean;
  hasPreviousPage?: boolean;
  isFetchingPreviousPage: boolean;
  fetchPreviousPage: () => void;
}

export const useMessagesScroll = ({
  conversationId,
  pageCount,
  lastMessageKey,
  isAnchorMode,
  hasPreviousPage,
  isFetchingPreviousPage,
  fetchPreviousPage
}: UseMessagesScrollParams) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isInitialLoad = useRef(true);
  const prevScrollHeightRef = useRef<number>(0);
  const isPrependingRef = useRef(false);

  useEffect(() => {
    isInitialLoad.current = true;
  }, [conversationId]);

  const handleLoadMoreBefore = useCallback(() => {
    if (!hasPreviousPage || isFetchingPreviousPage) return;
    const container = scrollContainerRef.current;
    if (container) {
      prevScrollHeightRef.current = container.scrollHeight;
      isPrependingRef.current = true;
    }
    fetchPreviousPage();
  }, [hasPreviousPage, isFetchingPreviousPage, fetchPreviousPage]);

  useLayoutEffect(() => {
    if (!isPrependingRef.current) return;
    const container = scrollContainerRef.current;
    if (!container) return;

    const addedHeight = container.scrollHeight - prevScrollHeightRef.current;
    container.scrollTop += addedHeight;
    isPrependingRef.current = false;
  }, [pageCount]);

  useEffect(() => {
    if (!lastMessageKey || isAnchorMode) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    if (isInitialLoad.current) {
      container.scrollTop = container.scrollHeight;
      isInitialLoad.current = false;
      return;
    }
    container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
  }, [lastMessageKey, isAnchorMode]);

  return { scrollContainerRef, handleLoadMoreBefore };
};
